import React from 'react';
import {
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { VblForecastFilters } from '@/types/vblForecast';
import { FilterTags } from '@/components/filters/FilterTags';
import { FilterTag } from '@/types/filters';

interface VblForecastFilterSidebarProps {
  filters: VblForecastFilters;
  onFiltersChange: (filters: VblForecastFilters) => void;
  availableOptions: {
    sites: string[];
    turbineModels: string[];
    componentsMajor: string[];
    componentsMinor: string[];
  };
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const VblForecastFilterSidebar: React.FC<VblForecastFilterSidebarProps> = ({
  filters,
  onFiltersChange,
  availableOptions,
  isCollapsed = false,
  onToggleCollapse,
}) => {

  const handleFilterChange = (field: keyof VblForecastFilters, values: string[]) => {
    onFiltersChange({
      ...filters,
      [field]: values
    });
  };

  // Generate filter tags for visual display
  const generateFilterTags = (): FilterTag[] => {
    const tags: FilterTag[] = [];

    // Sites
    filters.sites.forEach((site) => {
      tags.push({
        id: `site-${site}`,
        category: 'sites',
        label: 'Site',
        value: site,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
      });
    });

    // Turbine Models
    filters.turbineModels.forEach((model) => {
      tags.push({
        id: `model-${model}`,
        category: 'turbineModels',
        label: 'Model',
        value: model,
        color: 'bg-green-100 text-green-800 border-green-200',
      });
    });

    // Components
    filters.componentsMajor.forEach((comp) => {
      tags.push({
        id: `component-${comp}`,
        category: 'componentsMajor',
        label: 'Component',
        value: comp,
        color: 'bg-purple-100 text-purple-800 border-purple-200',
      });
    });

    // Sub-Components
    filters.componentsMinor.forEach((comp) => {
      tags.push({
        id: `subcomponent-${comp}`,
        category: 'componentsMinor',
        label: 'Sub-Component',
        value: comp,
        color: 'bg-orange-100 text-orange-800 border-orange-200',
      });
    });

    return tags;
  };

  const handleRemoveTag = (tagId: string) => {
    const tags = generateFilterTags();
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return;

    const category = tag.category as keyof VblForecastFilters;
    const currentValues = filters[category] || [];
    const newValues = currentValues.filter((value: string) => value !== tag.value);

    handleFilterChange(category, newValues);
  };

  // Calculate active filter count
  const activeFilterCount = Object.values(filters).reduce((count, filterArray) => {
    return count + (Array.isArray(filterArray) ? filterArray.length : 0);
  }, 0);

  if (isCollapsed) {
    return (
      <div className="bg-white border-r border-gray-200 h-full flex flex-col w-12">
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Expand VBL forecast filters"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="relative">
            <FunnelIcon className="h-6 w-6 text-gray-400" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      <div className="lg:hidden fixed inset-0 z-50 flex">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onToggleCollapse} />
        <div className="relative flex flex-col w-full max-w-xs bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              onClick={onToggleCollapse}
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Close filters</span>
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="h-0 flex-1 pt-5 pb-4 overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <FunnelIcon className="w-5 h-5 text-blue-600" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">VBL Forecast Filters</h3>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Active Filter Tags */}
              {activeFilterCount > 0 && (
                <FilterTags
                  tags={generateFilterTags()}
                  onRemoveTag={handleRemoveTag}
                  headerText="Active Filters"
                />
              )}

              {/* Mobile Filter Content */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Sites</label>
                <MultiSelectDropdown
                  options={availableOptions.sites.map(site => ({ value: site, label: site }))}
                  selectedValues={filters.sites}
                  onChange={(values) => handleFilterChange('sites', values)}
                  placeholder="All sites"
                  className="w-full"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Turbine Models</label>
                <MultiSelectDropdown
                  options={availableOptions.turbineModels.map(model => ({ value: model, label: model }))}
                  selectedValues={filters.turbineModels}
                  onChange={(values) => handleFilterChange('turbineModels', values)}
                  placeholder="All models"
                  className="w-full"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Components</label>
                <MultiSelectDropdown
                  options={availableOptions.componentsMajor.map(comp => ({ value: comp, label: comp }))}
                  selectedValues={filters.componentsMajor}
                  onChange={(values) => handleFilterChange('componentsMajor', values)}
                  placeholder="All components"
                  className="w-full"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Sub-Components</label>
                <MultiSelectDropdown
                  options={availableOptions.componentsMinor.map(comp => ({ value: comp, label: comp }))}
                  selectedValues={filters.componentsMinor}
                  onChange={(values) => handleFilterChange('componentsMinor', values)}
                  placeholder="All sub-components"
                  className="w-full"
                />
              </div>

              {/* Clear Filters Button */}
              {activeFilterCount > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => onFiltersChange({
                      sites: [],
                      turbineModels: [],
                      componentsMajor: [],
                      componentsMinor: []
                    })}
                    className="w-full px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Clear All Filters ({activeFilterCount})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex bg-white border-r border-gray-200 h-full flex-col w-80">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <FunnelIcon className="w-5 h-5 text-blue-600" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">VBL Forecast Filters</h3>
            </div>
            <button
              onClick={onToggleCollapse}
              className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title="Close VBL forecast filters"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600">
            Filter forecast results by turbine attributes
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-visible px-6 py-4 space-y-6">
          {/* Active Filter Tags */}
          {activeFilterCount > 0 && (
            <FilterTags
              tags={generateFilterTags()}
              onRemoveTag={handleRemoveTag}
              headerText="Active Filters"
            />
          )}

          {/* Site Filter */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Sites</label>
            <MultiSelectDropdown
              options={availableOptions.sites.map(site => ({ value: site, label: site }))}
              selectedValues={filters.sites}
              onChange={(values) => handleFilterChange('sites', values)}
              placeholder="All sites"
              className="w-full"
            />
          </div>

          {/* Turbine Model Filter */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Turbine Models</label>
            <MultiSelectDropdown
              options={availableOptions.turbineModels.map(model => ({ value: model, label: model }))}
              selectedValues={filters.turbineModels}
              onChange={(values) => handleFilterChange('turbineModels', values)}
              placeholder="All models"
              className="w-full"
            />
          </div>

          {/* Component Filter */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Components</label>
            <MultiSelectDropdown
              options={availableOptions.componentsMajor.map(comp => ({ value: comp, label: comp }))}
              selectedValues={filters.componentsMajor}
              onChange={(values) => handleFilterChange('componentsMajor', values)}
              placeholder="All components"
              className="w-full"
            />
          </div>

          {/* Sub-Component Filter */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Sub-Components</label>
            <MultiSelectDropdown
              options={availableOptions.componentsMinor.map(comp => ({ value: comp, label: comp }))}
              selectedValues={filters.componentsMinor}
              onChange={(values) => handleFilterChange('componentsMinor', values)}
              placeholder="All sub-components"
              className="w-full"
            />
          </div>

          {/* Clear Filters Button */}
          {activeFilterCount > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => onFiltersChange({
                  sites: [],
                  turbineModels: [],
                  componentsMajor: [],
                  componentsMinor: []
                })}
                className="w-full px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Clear All Filters ({activeFilterCount})
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default VblForecastFilterSidebar;